import { useState } from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import { useProps } from '@tamagui/core';

import { Toggle, ToggleProps } from '../Toggle';
import { TextInput, TextInputProps } from './TextInput';
import {
  ValidatedTextInput,
  ValidatedTextInputProps,
  ValidationState,
} from './ValidatedTextInput/ValidatedTextInput';

const { toggle: toggleTokens } = tokens;

type SharedToggleProps = {
  /**
   * Props to pass to the toggle component
   */
  toggleProps: Pick<ToggleProps, 'leftIcon' | 'rightIcon' | 'onCheckedChange' | 'defaultChecked'>;
  /**
   * Any additional children to render inside the text input on the right side
   * (left of the toggle)
   */
  children?: React.ReactNode;
};

export type TextInputWithToggleProps =
  | ValidatedTextInputWithToggleProps
  | (UnvalidatedTextInputWithToggleProps & {
      /**
       * When format is provided, ValidatedTextInput is used. Otherwise, TextInput is used.
       */
      format?: never;
    });

type UnvalidatedTextInputWithToggleProps = TextInputProps &
  SharedToggleProps & {
    /** The frameProps property is excluded in this type */
    frameProps?: never;
  };

type ValidatedTextInputWithToggleProps = ValidatedTextInputProps &
  SharedToggleProps & {
    /** The frameProps property is excluded in this type */
    frameProps?: never;
  };

/**
 * A text input that includes a toggle. Useful for, e.g., switching units
 * between dollars and percentages. Pass `toggleProps` to configure the toggle,
 * including `leftIcon` and `rightIcon` to render an icon on each side. For
 * example:
 *
 * ```tsx
 * import { TextInputWithToggle, Icons } from '@opendoor/bricks-next';
 *
 * const MyComponent = () => (
 *   <TextInputWithToggle
 *     toggleProps={{
 *       leftIcon: { Component: Icons.Dollar, label: 'dollars' },
 *       rightIcon: { Component: Icons.Percentage, label: 'percent' },
 *     }}
 *   />
 * );
 * ```
 *
 * Note that you must include a label for each of the icons for accessibility
 * (see below).
 *
 * Along with `leftIcon` and `rightIcon`, you can include `defaultChecked` to
 * set the initial state of the toggle and `onCheckedChange` to listen for
 * changes to the toggle state.
 *
 * Other props work the same as TextInput (or ValidatedTextInput).
 *
 * ** Accessibility **
 *
 * Keyboard interactions:
 *
 * - The tab key moves focus from the input to the toggle button.
 * - The space key and the enter key toggle the state of the toggle.
 *
 * Aria attributes:
 * - `aria-checked` is set to `true` or `false` based on the state of the toggle.
 * - `role` is set to "switch".
 * - `aria-label` is set for each of the icons.
 *
 * See: https://www.w3.org/WAI/ARIA/apg/patterns/switch/
 */
export const TextInputWithToggle = (props: TextInputWithToggleProps) => {
  if (props.format) {
    return <ValidatedTextInputWithToggle {...props} />;
  }
  return <UnvalidatedTextInputWithToggle {...props} />;
};

const getFrameProps = (size: TextInputProps['size']) => ({
  paddingVertical: toggleTokens.padding.textInput.topBottom[size],
  paddingLeft: toggleTokens.padding.textInput.left[size],
  paddingRight: toggleTokens.padding.textInput.right[size],
  gap: toggleTokens.padding.textInput.valueAndToggle.gap,
});

const UnvalidatedTextInputWithToggle = ({
  toggleProps,
  children,
  ...props
}: UnvalidatedTextInputWithToggleProps) => {
  const { disabled, errorText, invalid } = props;
  const { size = 'medium' } = useProps(props);

  return (
    <TextInput frameProps={getFrameProps(size)} {...props}>
      {children}
      <Toggle
        {...toggleProps}
        disabled={disabled}
        hasError={!!errorText || invalid}
        size={size === 'xsmall' ? 'small' : size}
        isEmbeddedInTextInput={true}
      />
    </TextInput>
  );
};

const ValidatedTextInputWithToggle = ({
  onValidationChange,
  toggleProps,
  children,
  ...props
}: ValidatedTextInputWithToggleProps) => {
  const [validationState, setValidationState] = useState<ValidationState>('unknown');

  const handleValidationChange = (validationState: ValidationState) => {
    setValidationState(validationState);
    onValidationChange?.(validationState);
  };

  const { disabled, errorText } = props;
  const { size = 'medium' } = useProps(props);

  return (
    <ValidatedTextInput
      onValidationChange={handleValidationChange}
      frameProps={getFrameProps(size)}
      {...props}
    >
      {children}
      <Toggle
        {...toggleProps}
        disabled={disabled}
        hasError={!!errorText || validationState === 'invalid'}
        size={size === 'xsmall' ? 'small' : size}
        isEmbeddedInTextInput={true}
      />
    </ValidatedTextInput>
  );
};
